import React, { useState } from 'react';

import { Box, Button, Group, InputBase, Popover, Select, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';

import { defaultPresets, getPresetsByKeys, Preset, PresetKey } from './presets';

export type Period = {
  period: string;
  startDate: Date | null;
  endDate: Date | null;
};

export type DateRangePickerProps = {
  className?: string;
  presets?: PresetKey[];
  value: Period;
  onChange: (value: Period) => void;
  label?: string;
  placeholder?: string;
};

const formatInput = (value: [Date | null, Date | null]): string => {
  if (!value[0] || !value[1]) return '';
  return `${dayjs(value[0]).format('MM/DD/YYYY')} - ${dayjs(value[1]).format('MM/DD/YYYY')}`;
};

const DateRangePicker = ({
  className,
  presets,
  value,
  onChange,
  label,
  placeholder = 'Select date range',
}: DateRangePickerProps): JSX.Element => {
  const [open, setOpened] = useState(false);
  const [draft, setDraft] = useState<Period>(value);

  const resolvedPresets: Preset[] = presets ? getPresetsByKeys(presets) : defaultPresets;

  const formatted = formatInput([value.startDate, value.endDate]);
  const isCustomPresetWithIncompleteRange =
    draft.period === 'custom' && (!draft.startDate || !draft.endDate);

  const handlePresetChange = (val: string | null): void => {
    if (!val) return;
    const found = resolvedPresets.find(p => p.value === val);
    const range = found ? found.getRange() : [null, null];
    setDraft({ period: val, startDate: range[0], endDate: range[1] });
  };

  const handleDateChange = (range: [Date | null, Date | null]): void => {
    const matched = resolvedPresets.find(p => {
      const [ps, pe] = p.getRange();
      return dayjs(ps).isSame(range[0], 'day') && dayjs(pe).isSame(range[1], 'day');
    });

    setDraft({ period: matched ? matched.value : 'custom', startDate: range[0], endDate: range[1] });
  };

  const handleCancel = (): void => {
    setDraft(value);
    setOpened(false);
  };

  const handleUpdate = (): void => {
    onChange(draft);
    setOpened(false);
  };

  return (
    <Popover opened={open} onClose={() => setOpened(false)} withArrow>
      <Popover.Target>
        <InputBase
          className={className}
          label={label}
          value={formatted}
          placeholder={placeholder}
          readOnly
          onClick={() => setOpened(o => !o)}
        />
      </Popover.Target>
      <Popover.Dropdown style={{ maxWidth: '100%' }}>
        <Box>
          <Group align='flex-start' sx={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap' }} mb='md'>
            <Text size='sm' pt='0.5rem'>
              Date Range
            </Text>
            <Select
              data={[
                ...resolvedPresets.map(preset => ({ value: preset.value, label: preset.label })),
                { value: 'custom', label: 'Custom' },
              ]}
              value={draft.period}
              onChange={handlePresetChange}
            />
            <DatePickerInput
              type='range'
              error={isCustomPresetWithIncompleteRange ? 'Please select a date range' : undefined}
              value={[draft.startDate, draft.endDate]}
              valueFormat='MM-DD-YYYY'
              labelSeparator='-'
              onChange={handleDateChange}
              allowSingleDateInRange
            />
          </Group>

          <Group sx={{ display: 'flex', justifyContent: 'flex-end' }} mt='md'>
            <Button variant='subtle' onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isCustomPresetWithIncompleteRange}>
              Update
            </Button>
          </Group>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default DateRangePicker;
